import React, { useContext, useState, useEffect, ReactNode } from "react";
import { jwtDecode } from "jwt-decode";
import { auth } from "../firebase";
import firebase from "firebase/compat/app";

// Interfaces
interface AuthContextType {
  token: string | null;
  role: string | null;
  sucursal: string | null;
  setToken: (token: string | null) => void;
  currentUser: firebase.User | null;
  setCurrentUser: (user: firebase.User | null) => void;
  loading: boolean;
  permisos: Record<string, any>;
  setPermisos: (permisos: Record<string, any>) => void;
  isAdmin: boolean;
  setIsAdmin: (isAdmin: boolean) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface DecodedToken {
  role?: string;
  sucursal?: string;
  [key: string]: any;
}

// Crea el contexto
const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Crea el proveedor del contexto
export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [sucursal, setSucursal] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<firebase.User | null>(null);
  const [loading, setLoading] = useState(true);
  const [permisos, setPermisos] = useState<Record<string, any>>({});
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userToken = await user.getIdToken(true);
        setToken(userToken);
        const decodedToken: DecodedToken = jwtDecode(userToken);
        setRole(decodedToken.role || null);
        setSucursal(decodedToken.sucursal || null);
        // Solo Director General es admin
        const isDirectorGeneral = decodedToken.role === 'Director General';
        setIsAdmin(isDirectorGeneral);
        
        // Si es Director General, establecemos todos los permisos
        if (isDirectorGeneral) {
          setPermisos({
            bienvenida: true,
            perfil: true,
            sucursales: true,
            agenda: true,  // Añadimos permiso de agenda
            empleado: {
              buscar: true,
              agregar: true,
              eliminar: true
            },
            cliente: {
              buscar: true,
              agregar: true
            },
            proyectos: {
              buscar: true,
              agregar: true,
              estructura: {
                buscar: true,
                agregar: true
              },
              concreto: {
                buscar: true,
                agregar: true
              }
            },
            capacitacion: {
              buscar: true,
              agregar: true,
              normas: true
            },
            solicitudes: true,
            vehiculos: {
              buscar: true,
              agregar: true,
              carga: true
            },
            recursos_humanos: {
              solicitudes: {
                buscar: true,
                agregar: true
              },
              asistencias: {
                buscar: true,
                agregar: true
              }
            },
            configuracion: true,
            chat: true
          });
        } else {
          // Para otros usuarios, establecer permisos básicos por defecto
          setPermisos({
            bienvenida: true,
            perfil: true,
            agenda: true
          });
        }
        
        setCurrentUser(user);
        setLoading(false);
      } else {
        setCurrentUser(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [])

  const value: AuthContextType = {
    token,
    role,
    sucursal,
    setToken,
    currentUser, 
    setCurrentUser,
    loading,
    permisos,
    setPermisos,
    isAdmin,
    setIsAdmin
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export const Context = AuthContext; // Para compatibilidad con imports antiguos